<template>
  <div>
    <div class="setup-hub-toggle" v-if="showSetupHubToggle" @click="openSidebar()">
      <div class="d-flex align-center justify-space-around">
        <div class="d-flex align-center mr-2">
          <template v-if="stepCompletedFromWebsocket">
            <v-icon icon="$circleCheck" size="24" />
            <div class="ml-1 subtitle2 text-white">
              {{ $t(`setupHub.subSteps.${stepCompletedFromWebsocket}.title`) }}
            </div>
            <div class="ml-1 subtitle2 text-white">
              {{ $t(`setupHub.activated`) }}
            </div>
          </template>
          <template v-else>
            <v-icon icon="$setupHub" size="24" />
            <div class="ml-1 subtitle2 text-white">
              {{ $t("setupHub.title") }}
            </div>
          </template>
        </div>

        <v-progress-linear
          color="green-base"
          :model-value="completedSteps"
          :max="totalSteps"
          rounded
          class="setup-hub-toggle__progress mr-2"
        />

        <div class="subtitle2 text-white">{{ completedSteps }}/{{ totalSteps }}</div>

        <div class="ml-2 coro-link text-white" v-if="stepCompletedFromWebsocket">
          {{ $t("setupHub.continueSetup") }}
        </div>
      </div>
    </div>

    <setup-hub-sidebar v-if="isPanelOpen" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, watch } from "vue";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { storeToRefs } from "pinia";
import SetupHubSidebar from "@/components/setup-hub/sidebar/SetupHubSidebar.vue";
import { SidebarId, useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import { SubscriptionType } from "@/constants/workplaces.ts";
import { useSetupHubStore } from "@/_store/setup-hub.module.ts";
import { trackGtmEvent } from "@/_helpers/utils.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";

export default defineComponent({
  components: { SetupHubSidebar },
  setup() {
    const workspaceStore = useWorkspaceStore();
    const sidebarPanelStore = useSidebarPanelStore();
    const accountStore = useAccountStore();
    const setupHubStore = useSetupHubStore();
    const appsMgmtStore = useAppsManagementModule();
    const { isMspContext } = storeToRefs(appsMgmtStore);
    const { data: setupHubData } = storeToRefs(setupHubStore);
    const { totalSteps, completedSteps, stepCompletedFromWebsocket } = storeToRefs(setupHubStore);
    const { workspace, workspaceAndPermissionsUpdatedTimestamp } = storeToRefs(workspaceStore);
    const { logged } = storeToRefs(accountStore);
    const isPanelOpen = computed(() => sidebarPanelStore.isOpen(SidebarId.SETUP_HUB));

    const showSetupHubToggle = computed(() => {
      const isAiAssistantOpened = sidebarPanelStore.isOpen(SidebarId.AI_ASSISTANT);
      const demoModeDisabled = !workspace.value.demoModeEnabled;
      const subscriptionsToDisplayOnboarding = [
        SubscriptionType.SUBSCRIPTION,
        SubscriptionType.TRIAL,
        SubscriptionType.INITIAL,
      ];
      const isProperSubscription = subscriptionsToDisplayOnboarding.includes(
        workspace.value.subscriptionType as SubscriptionType
      );
      const isSetupHubDataValid = setupHubData.value && !setupHubData.value.dismissed;
      const isMspRoute = isMspContext.value;
      return (
        !isAiAssistantOpened &&
        isSetupHubDataValid &&
        demoModeDisabled &&
        logged.value &&
        isProperSubscription &&
        !isPanelOpen.value &&
        !isMspRoute
      );
    });

    const openSidebar = () => {
      trackGtmEvent(GtmAction.SETUP_HUB, GTMParams.OPENED);
      sidebarPanelStore.toggleSidebar(SidebarId.SETUP_HUB);
    };

    onMounted(async () => {
      if (logged.value && workspace.value.workspaceId) {
        await setupHubStore.getOnboardingStatus();
      }
    });

    watch(workspaceAndPermissionsUpdatedTimestamp, async (newVal) => {
      if (newVal) {
        await setupHubStore.getOnboardingStatus();
      }
    });

    return {
      stepCompletedFromWebsocket,
      isPanelOpen,
      showSetupHubToggle,
      workspace,
      openSidebar,
      totalSteps,
      completedSteps,
    };
  },
});
</script>

<style scoped lang="scss">
.setup-hub-toggle {
  cursor: pointer;
  position: fixed;
  bottom: 24px;
  right: 24px;
  background-color: rgb(var(--v-theme-indigo-purple));
  color: rgb(var(--v-theme-white));
  padding: 8px 16px 8px 8px;
  align-items: center;
  gap: 16px;
  border-radius: 122px;
  box-shadow: 0 4px 16px 0 rgb(var(--v-theme-gray-medium));
  height: 40px;
  min-width: 212px;
  z-index: 999;
  &__progress {
    width: 32px;
  }
}

.setup-hub-sidebar {
  background-color: rgb(var(--v-theme-white));
  top: 56px;
  right: 0;
  height: calc(100vh - 56px);
  z-index: 1000;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.12) !important;
}

.icon-success-xs::before {
  color: rgb(var(--v-theme-green-light));
}

.icon-protection-xs::before {
  color: rgb(var(--v-theme-white));
}
</style>
